import * as React from 'react';
import { SelectValue } from '../../../../Components/Base/Select/Select';
import {
	useLazyDoctorBasicInfoQuery,
	useLazyWorkplaceDoctorsPrefetchQuery,
} from '../../../../graphql/graphql';
import { useAppContext } from '../../../../Services/AppContext';
import { FieldError } from 'react-hook-form';
import { useEffect, useRef, useState } from 'react';
import useWorkplaces from '../../../Workplace/Hooks/useWorkplaces';
import ReactSelect, { SingleValue } from 'react-select';
import { useEffectAsync } from '../../../../helpers';
import useTranslation from '../../../../Hooks/useTranslation';

interface DoctorAsyncSelectProps {
	onChange?: (value: SelectValue) => void;
	value?: SelectValue;
	error?: FieldError;
	placeholder?: string;
	searchInAllWorkplaces?: boolean;
}

const DoctorAsyncSelect: React.FC<DoctorAsyncSelectProps> = props => {
	const {
		value,
		onChange,
		placeholder = 'base.shared.pick',
		searchInAllWorkplaces = false,
	} = props;
	const [innerValue, setInnerValue] = useState<SelectValue | null>(null);
	const [selectInputValue, setSelectInputValue] = useState<string>('');
	const [options, setOptions] = useState<SelectValue[]>([]);
	const pageRef = useRef(1);
	const latestSearchRef = useRef<string>('');

	const [searchDoctors] = useLazyWorkplaceDoctorsPrefetchQuery();
	const [getDoctorInfo] = useLazyDoctorBasicInfoQuery();
	const { workplaceId } = useAppContext();
	const { workplaceIds } = useWorkplaces();
	const { t } = useTranslation();

	// Hide the default chevron and the vertical bar so the control looks like a plain field
	const components = {
		DropdownIndicator: () => null,
		IndicatorSeparator: () => null,
	};

	const handleChange = (val: SingleValue<SelectValue>) => {
		if (val) {
			onChange?.(val);
		}
	};

	const handleSearchItem = async (
		searchInput: string,
		page = 1,
	): Promise<SelectValue[]> => {
		const { workplaceDoctors } = await searchDoctors({
			fulltext: searchInput,
			workplaceIds: searchInAllWorkplaces ? workplaceIds : [workplaceId],
			pagination: {
				page,
			},
		}).unwrap();

		const items: SelectValue[] = [];

		for (const workplaceDoctor of workplaceDoctors?.data || []) {
			if (workplaceDoctor && workplaceDoctor.user) {
				if (!items.some(item => item.value === workplaceDoctor?.user?.id)) {
					items.push({
						value: workplaceDoctor.user.id,
						label: workplaceDoctor.user.profile?.fullName || '',
					});
				}
			}
		}

		return items;
	};

	const handleLoadOptionsBySearch = async (inputValue: string) => {
		latestSearchRef.current = inputValue;
		pageRef.current = 1;
		const newOptions = await handleSearchItem(inputValue);
		if (latestSearchRef.current === inputValue) {
			setOptions(newOptions);
		}
	};

	const handleLoadMoreOptions = async () => {
		const nextPage = pageRef.current + 1;
		pageRef.current = nextPage;
		const newOptions = await handleSearchItem(selectInputValue, nextPage);

		setOptions(prevOptions => {
			const existingValues = new Set(prevOptions.map(o => o.value));
			const dedupedNew = newOptions.filter(o => !existingValues.has(o.value));
			return [...prevOptions, ...dedupedNew];
		});
	};

	useEffectAsync(async () => {
		await handleLoadOptionsBySearch(selectInputValue);
	}, [selectInputValue, workplaceId, workplaceIds, searchInAllWorkplaces]);

	useEffect(() => {
		setInnerValue(value ?? null);
	}, [value]);

	useEffectAsync(async () => {
		if (value?.value && !value?.label) {
			const { user } = await getDoctorInfo({
				id: String(value.value),
			}).unwrap();

			if (user?.profile?.fullName) {
				onChange?.({ value: user.id, label: user.profile.fullName });
			}
		}
	}, [value?.value]);

	return (
		<ReactSelect
			classNamePrefix={'reactSearchSelect'}
			options={options}
			value={innerValue}
			onChange={handleChange}
			menuPortalTarget={document.body}
			isSearchable={true}
			onMenuScrollToBottom={handleLoadMoreOptions}
			onInputChange={setSelectInputValue}
			components={components}
			placeholder={t(placeholder)}
		/>
	);
};

export default DoctorAsyncSelect;
